"use client"

import { FormEvent, KeyboardEvent, useRef, useEffect } from "react"
import { Loader2Icon, ArrowUp, Camera } from "lucide-react"

interface ChatInputProps {
  input: string
  setInput: (input: string) => void
  handleSubmit: (e: FormEvent) => void
  isLoading: boolean
  onImageSelect?: (base64: string) => void
  className?: string
}

export function ChatInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  onImageSelect,
  className = "",
}: ChatInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "0px"
      const scrollHeight = textarea.scrollHeight
      textarea.style.height = Math.min(scrollHeight, 160) + "px"
    }
  }, [input])

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (e.ctrlKey || e.shiftKey) {
        setInput(`${input}\n`)
        e.preventDefault()
      } else {
        if (input.trim()) {
          e.preventDefault()
          handleSubmit(e as unknown as FormEvent)
        }
      }
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !onImageSelect) return

    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Strip the data:image/...;base64, prefix
      const base64 = result.split(",")[1]
      if (base64) onImageSelect(base64)
    }
    reader.readAsDataURL(file)

    // Reset so the same file can be selected again
    e.target.value = ""
  }

  const hasInput = input.trim().length > 0

  return (
    <div className={`px-4 pb-4 pt-2 w-full ${className}`}>
      <form
        onSubmit={handleSubmit}
        className="flex items-center w-full bg-gray-50 rounded-2xl border border-gray-200 focus-within:border-emerald-300 focus-within:ring-2 focus-within:ring-emerald-100 transition-all"
      >
        {/* Camera button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading}
          className="flex-shrink-0 w-9 h-9 ml-1.5 rounded-xl flex items-center justify-center text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
          title="Scan a food label"
        >
          <Camera className="h-4.5 w-4.5" />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
        />

        <textarea
          ref={textareaRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about halal food in Japan..."
          disabled={isLoading}
          className="flex-1 bg-transparent border-none outline-none resize-none px-3 py-3 text-sm text-gray-800 placeholder:text-gray-400 min-h-[44px] max-h-[160px] focus:ring-0"
          rows={1}
          autoFocus
        />

        {/* Send button */}
        <button
          type="submit"
          disabled={!hasInput || isLoading}
          className={`flex-shrink-0 w-9 h-9 mr-1.5 rounded-xl flex items-center justify-center transition-all ${
            hasInput && !isLoading
              ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          {isLoading ? (
            <Loader2Icon className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowUp className="h-4 w-4" />
          )}
        </button>
      </form>

      <p className="text-[10px] text-gray-300 text-center mt-1.5">
        Tayyib may make mistakes. Verify halal status with a certification body.
      </p>
    </div>
  )
}
